/**
 * Central de Atendimento (modo mock): conversas, mensagens, atendentes,
 * etiquetas e respostas rápidas. Horários são relativos ao carregamento da
 * página, para a lista sempre parecer "de hoje".
 */
import type {
  CannedResponse,
  ChatwootLabel,
  ChatwootMessage,
  Conversation,
  SupportAgent,
  ThreadHistory,
} from '@/api/endpoints/support';

const NOW = Math.floor(Date.now() / 1000);
const ago = (min: number) => NOW - Math.round(min * 60);

export const ME = { id: 2, name: 'Mikael David' };

export const supportAgents: SupportAgent[] = [
  {
    chatwootUserId: 2,
    managerId: 2,
    name: 'Mikael David',
    email: 'claude.agent@curraltech.com.br',
  },
  { chatwootUserId: 4, managerId: 4, name: 'Larissa Moura', email: 'larissa@curraltech.com.br' },
  { chatwootUserId: 5, managerId: 5, name: 'Rafael Antunes', email: 'rafael@curraltech.com.br' },
  { chatwootUserId: 6, managerId: 6, name: 'Camila Duarte', email: 'camila@curraltech.com.br' },
];

const agentById = (id: number) => supportAgents.find((a) => a.chatwootUserId === id)!;

export const supportLabels: ChatwootLabel[] = [
  { id: 1, title: 'tecnico', color: '#3B82F6', description: 'Suporte técnico' },
  { id: 2, title: 'financeiro', color: '#F59E0B', description: 'Financeiro' },
  { id: 3, title: 'novo-usuario', color: '#10B981', description: 'Novo usuário' },
  { id: 4, title: 'compra-brinco', color: '#EAB308', description: 'Compra de brinco' },
  { id: 5, title: 'compra-leitor', color: '#8B5CF6', description: 'Compra de leitor' },
  { id: 6, title: 'nao-identificado', color: '#64748B', description: 'Não identificado' },
  { id: 7, title: 'triagem-incompleta', color: '#EF4444', description: 'Triagem incompleta' },
];

export const supportCanned: CannedResponse[] = [
  {
    id: 1,
    short_code: 'saudacao',
    content: 'Olá! Aqui é da equipe Curral Tech. Em que posso ajudar?',
  },
  {
    id: 2,
    short_code: 'sincronizar',
    content:
      'Para enviar os dados do celular, abra o app com internet, toque no menu lateral e depois em Sincronizar. Pode levar alguns minutos se houver muitos animais.',
  },
  {
    id: 3,
    short_code: 'leitor',
    content:
      'Para parear o leitor: ligue o Bluetooth e a localização do celular, abra Configurações, Leitor RFID, e toque em Procurar. Mantenha o leitor a menos de 1 metro.',
  },
  {
    id: 4,
    short_code: 'codigo',
    content:
      'O acesso ao app é pelo código que chega aqui no WhatsApp. Na tela de entrada, informe seu número e toque em Receber código.',
  },
  {
    id: 5,
    short_code: 'planos',
    content:
      'Hoje temos o plano Básico (R$ 39,90/mês, até 300 animais) e o Profissional (R$ 99,90/mês, até 1.500 animais). Os dois têm 15 dias de teste grátis.',
  },
  {
    id: 6,
    short_code: 'encerrar',
    content: 'Fico feliz em ajudar. Se precisar de mais alguma coisa, é só chamar por aqui.',
  },
];

// ---------------------------------------------------------------------------
// Construção das conversas
// ---------------------------------------------------------------------------

type Kind = 'in' | 'bot' | 'note' | 'activity' | number; // number = id do atendente
type Linha = [minutosAtras: number, quem: Kind, texto: string];

const BOT = { id: 900, name: 'Assistente Curral Tech', type: 'agent_bot' };
const MENU_BOT =
  'Olá! Sou o assistente da Curral Tech. Sobre o que você quer falar?\n1. Suporte técnico\n2. Financeiro\n3. Sou novo por aqui\n4. Comprar brincos ou leitor';

let nextMsgId = 10_000;
export const novoIdMensagem = () => ++nextMsgId;

function montarMensagens(
  contato: { id: number; name: string },
  linhas: Linha[],
  conversationId: number
): ChatwootMessage[] {
  return linhas.map(([min, quem, texto]) => {
    const base = {
      id: novoIdMensagem(),
      content: texto,
      created_at: ago(min),
      conversation_id: conversationId,
    };
    if (quem === 'in') {
      return {
        ...base,
        message_type: 0,
        sender: { id: contato.id, name: contato.name, type: 'contact' },
      };
    }
    if (quem === 'bot') {
      return { ...base, message_type: 1, sender: BOT, status: 'read' };
    }
    if (quem === 'note') {
      return { ...base, message_type: 1, private: true, sender: BOT };
    }
    if (quem === 'activity') {
      return { ...base, message_type: 2 };
    }
    const a = agentById(quem);
    return {
      ...base,
      message_type: 1,
      sender: { id: a.chatwootUserId, name: a.name, type: 'user' },
      status: min > 3 ? 'read' : 'delivered',
    };
  });
}

interface Spec {
  id: number;
  status: 'open' | 'pending' | 'snoozed' | 'resolved';
  contato: { id: number; name: string; phone_number: string };
  assigneeId: number | null;
  unread: number;
  labels: string[];
  attrs?: Conversation['custom_attributes'];
  linhas: Linha[];
}

const specs: Spec[] = [
  {
    id: 501,
    status: 'open',
    contato: { id: 7001, name: 'João Batista Ferreira', phone_number: '+5567989352424' },
    assigneeId: 2,
    unread: 2,
    labels: ['tecnico'],
    attrs: {
      bot_category: 'tecnico',
      bot_identified: true,
      bot_producer_name: 'João Batista Ferreira',
      bot_plano: 'Profissional',
      bot_problem: 'Leitor RFID liga, mas o aplicativo não encontra o aparelho pelo Bluetooth.',
    },
    linhas: [
      [96, 'in', 'Bom dia, tudo bem?'],
      [96, 'bot', MENU_BOT],
      [95, 'in', '1'],
      [95, 'bot', 'Certo. Descreva rapidamente o problema que um atendente já vai te ajudar.'],
      [
        94,
        'in',
        'O leitor de brinco não está conectando no celular. Liguei, aparece a luz azul, mas o app não acha',
      ],
      [
        94,
        'note',
        'Triagem: suporte técnico. Produtor identificado: João Batista Ferreira (Profissional). Problema: leitor RFID não pareia pelo Bluetooth.',
      ],
      [90, 'activity', 'Mikael David assumiu a conversa'],
      [88, 2, 'Bom dia, seu João! Aqui é o Mikael, da Curral Tech. Vou te ajudar com o leitor.'],
      [
        87,
        2,
        'O senhor consegue me dizer qual o modelo do leitor? Fica escrito na etiqueta atrás dele.',
      ],
      [71, 'in', 'É o bastão preto, está escrito RS420'],
      [
        68,
        2,
        'Perfeito. Faz o seguinte: abre o app, vai em Configurações, depois Leitor RFID, e toca em Esquecer dispositivo. Depois desliga o leitor, espera 10 segundos e liga de novo.',
      ],
      [
        66,
        2,
        'Confere também se o Bluetooth e a localização do celular estão ligados. O Android pede a localização para encontrar o leitor.',
      ],
      [9, 'in', 'Fiz tudo isso, agora apareceu o leitor na lista'],
      [6, 'in', 'Mas quando passo no brinco ele apita e não aparece nada na tela do manejo'],
    ],
  },
  {
    id: 502,
    status: 'open',
    contato: { id: 7002, name: 'Marta Regina Costa', phone_number: '+5567950384923' },
    assigneeId: 4,
    unread: 0,
    labels: ['financeiro'],
    attrs: {
      bot_category: 'financeiro',
      bot_identified: true,
      bot_producer_name: 'Marta Regina Costa',
      bot_plano: 'Básico',
      bot_problem: 'Diz que foi cobrada duas vezes na Play Store neste mês.',
    },
    linhas: [
      [305, 'in', 'Boa tarde. Fui cobrada duas vezes na Play Store este mês'],
      [305, 'bot', MENU_BOT],
      [304, 'in', '2'],
      [304, 'bot', 'Entendi. Conte o que aconteceu que já chamo alguém do financeiro.'],
      [303, 'in', 'Apareceram duas cobranças de 39,90 no cartão'],
      [
        303,
        'note',
        'Triagem: financeiro. Produtora identificada: Marta Regina Costa (Básico, Google Play).',
      ],
      [282, 'activity', 'Larissa Moura assumiu a conversa'],
      [
        280,
        4,
        'Boa tarde, dona Marta. Sou a Larissa, do financeiro. Pode me mandar um print das duas cobranças?',
      ],
      [252, 'in', 'Mandei por e-mail agora pouco, para o suporte@curraltech.com.br'],
      [
        241,
        4,
        'Recebi, obrigada. Uma das cobranças é a renovação do plano Básico e a outra é uma pré-autorização do Google, que cai sozinha em até 3 dias úteis.',
      ],
      [239, 4, 'Se não cair até sexta, me avisa aqui que eu abro o reembolso direto com o Google.'],
      [236, 'in', 'Ah, entendi. Obrigada pela atenção'],
      [234, 4, 'Por nada! Qualquer coisa estou por aqui.'],
    ],
  },
  {
    id: 503,
    status: 'pending',
    contato: { id: 7003, name: 'Edson Luiz Macedo', phone_number: '+5567966152964' },
    assigneeId: null,
    unread: 1,
    labels: ['novo-usuario'],
    attrs: {
      bot_category: 'novo-usuario',
      bot_identified: true,
      bot_producer_name: 'Edson Luiz Macedo',
      bot_plano: 'Profissional',
      bot_problem: 'Quer saber como cadastrar os funcionários para usarem o app no celular deles.',
    },
    linhas: [
      [21, 'in', 'Oi, baixei o aplicativo ontem'],
      [21, 'bot', MENU_BOT],
      [20, 'in', '3'],
      [
        20,
        'bot',
        'Que bom ter você com a gente! Conte o que você precisa e um atendente já te responde.',
      ],
      [
        14,
        'in',
        'Queria saber como cadastro os meus funcionários para eles usarem no celular deles também',
      ],
      [13, 'bot', 'Obrigado. Já avisei a equipe, em instantes alguém te responde.'],
    ],
  },
  {
    id: 504,
    status: 'pending',
    contato: { id: 7004, name: 'Leandro Furtado', phone_number: '+5565999812233' },
    assigneeId: null,
    unread: 3,
    labels: ['compra-brinco', 'nao-identificado'],
    attrs: {
      bot_category: 'compra-brinco',
      bot_identified: false,
      bot_problem: 'Tem 850 cabeças de nelore em Cáceres (MT) e quer orçamento de brinco e leitor.',
    },
    linhas: [
      [46, 'in', 'Boa tarde, vocês vendem brinco eletrônico?'],
      [46, 'bot', MENU_BOT],
      [45, 'in', '4'],
      [45, 'bot', 'Ótimo. Para quantos animais e em qual cidade fica a fazenda?'],
      [
        41,
        'in',
        'Tenho 850 cabeças de nelore aqui em Cáceres, MT. Queria orçamento de brinco e de um leitor',
      ],
      [40, 'in', 'Se tiver aplicador também me manda o preço'],
      [39, 'bot', 'Anotado! Um consultor vai te chamar com o orçamento.'],
    ],
  },
  {
    id: 505,
    status: 'open',
    contato: { id: 7005, name: 'Cleusa Maria', phone_number: '+5567951474910' },
    assigneeId: 2,
    unread: 0,
    labels: ['tecnico'],
    attrs: {
      pinned: true,
      bot_category: 'tecnico',
      bot_identified: true,
      bot_producer_name: 'Cleusa Maria',
      bot_plano: 'Profissional',
      bot_problem: 'Animais cadastrados no celular do vaqueiro não aparecem no celular dela.',
    },
    linhas: [
      [182, 'in', 'Os animais que eu cadastrei no celular do meu vaqueiro não aparecem no meu'],
      [
        181,
        'note',
        'Triagem: suporte técnico. Produtora identificada: Cleusa Maria (Profissional). Possível sincronização pendente no aparelho do funcionário.',
      ],
      [176, 'activity', 'Mikael David assumiu a conversa'],
      [
        172,
        2,
        'Oi, dona Cleusa. Isso costuma ser sincronização pendente. Pede para o vaqueiro abrir o app com internet e tocar em Sincronizar, no menu lateral.',
      ],
      [121, 'in', 'Ele está no retiro, lá não pega sinal. Volta só amanhã'],
      [
        118,
        2,
        'Sem problema. Os dados ficam guardados no celular dele. Assim que ele pegar sinal e sincronizar, aparecem no seu. Deixei sua conversa fixada para eu acompanhar amanhã.',
      ],
      [116, 'in', 'Combinado, muito obrigada'],
      [
        42,
        2,
        'Dona Cleusa, só confirmando: o celular dele é Android ou iPhone? Quero verificar se a versão do app está atualizada.',
      ],
    ],
  },
  {
    id: 506,
    status: 'snoozed',
    contato: { id: 7006, name: 'Nair Fontoura', phone_number: '+5567942913647' },
    assigneeId: 2,
    unread: 0,
    labels: ['financeiro'],
    attrs: {
      bot_category: 'financeiro',
      bot_identified: true,
      bot_producer_name: 'Nair Fontoura',
      bot_plano: 'Profissional',
    },
    linhas: [
      [1590, 'in', 'Quero mudar do plano mensal para o anual, como faço?'],
      [1585, 'activity', 'Mikael David assumiu a conversa'],
      [
        1580,
        2,
        'Oi, dona Nair. A troca é pela própria App Store: Ajustes, seu nome, Assinaturas, Curral Tech. Lá aparece a opção anual com desconto.',
      ],
      [1560, 'in', 'Vou ver com meu filho no fim de semana e te falo'],
      [1555, 2, 'Combinado. Vou deixar a conversa pausada e retomo na segunda.'],
    ],
  },
  {
    id: 507,
    status: 'resolved',
    contato: { id: 7007, name: 'Benedito Carvalho', phone_number: '+5567917905073' },
    assigneeId: 5,
    unread: 0,
    labels: ['tecnico'],
    attrs: {
      bot_category: 'tecnico',
      bot_identified: true,
      bot_producer_name: 'Benedito Carvalho',
    },
    linhas: [
      [1500, 'in', 'Bom dia. Esqueci a senha do aplicativo'],
      [1490, 'activity', 'Rafael Antunes assumiu a conversa'],
      [
        1488,
        5,
        'Bom dia, seu Benedito. O app não usa senha: o acesso é pelo código que chega aqui no WhatsApp. Na tela de entrada, coloca seu número e toca em Receber código.',
      ],
      [1475, 'in', 'Deu certo, entrei'],
      [1473, 5, 'Ótimo! Vou encerrar por aqui, qualquer coisa é só chamar.'],
      [
        1472,
        'bot',
        'Seu atendimento foi encerrado. Se precisar de algo, é só mandar uma mensagem por aqui.',
      ],
    ],
  },
  {
    id: 508,
    status: 'resolved',
    contato: { id: 7008, name: 'Iracema Barbosa', phone_number: '+5567986661128' },
    assigneeId: 6,
    unread: 0,
    labels: ['compra-leitor'],
    attrs: {
      bot_category: 'compra-leitor',
      bot_identified: true,
      bot_producer_name: 'Iracema Barbosa',
      bot_plano: 'Profissional',
    },
    linhas: [
      [4380, 'in', 'Preciso de mais um leitor para a segunda fazenda'],
      [4370, 'activity', 'Camila Duarte assumiu a conversa'],
      [
        4365,
        6,
        'Olá, dona Iracema. Mandei no seu e-mail o orçamento do leitor RS420 com frete para Goiânia.',
      ],
      [4300, 'in', 'Recebi, pode fechar. Vou pagar no boleto'],
      [
        4295,
        6,
        'Perfeito, o boleto vence em 3 dias úteis. Assim que compensar, despachamos pelos Correios.',
      ],
      [
        4290,
        'bot',
        'Seu atendimento foi encerrado. Se precisar de algo, é só mandar uma mensagem por aqui.',
      ],
    ],
  },
];

export interface MockConversation {
  conversation: Conversation;
  messages: ChatwootMessage[];
}

/** Atualiza os campos derivados (prévia, horário, espera) depois de mexer nas mensagens. */
export function recalcularConversa(c: MockConversation) {
  const reais = c.messages.filter((m) => m.message_type !== 2 && !m.private);
  const ultima = reais[reais.length - 1];
  const ultimaQualquer = c.messages[c.messages.length - 1];
  c.conversation.last_non_activity_message = ultima;
  c.conversation.messages = ultimaQualquer ? [ultimaQualquer] : [];
  c.conversation.timestamp = ultimaQualquer?.created_at;
  c.conversation.last_activity_at = ultimaQualquer?.created_at;
  const aguardando = ultima?.message_type === 0 && c.conversation.status !== 'resolved';
  if (aguardando) {
    // Espera conta desde a primeira mensagem do cliente sem resposta.
    let desde = ultima.created_at;
    for (let i = reais.length - 1; i >= 0 && reais[i].message_type === 0; i--) {
      desde = reais[i].created_at;
    }
    c.conversation.waiting_since = desde;
  } else {
    c.conversation.waiting_since = undefined;
  }
}

export const supportConversations: MockConversation[] = specs.map((s) => {
  const messages = montarMensagens(s.contato, s.linhas, s.id);
  const assignee = s.assigneeId != null ? agentById(s.assigneeId) : null;
  const item: MockConversation = {
    conversation: {
      id: s.id,
      status: s.status,
      unread_count: s.unread,
      labels: s.labels,
      custom_attributes: { ...(s.attrs ?? {}) },
      created_at: messages[0]?.created_at,
      meta: {
        sender: s.contato,
        assignee: assignee ? { id: assignee.chatwootUserId, name: assignee.name } : null,
      },
    },
    messages,
  };
  recalcularConversa(item);
  return item;
});

// Atendimento anterior do João (antes da inbox travar uma conversa por contato).
const anteriorJoao = montarMensagens(
  { id: 7001, name: 'João Batista Ferreira' },
  [
    [57_600, 'in', 'Boa tarde, como faço para lançar a vacina de aftosa no lote todo de uma vez?'],
    [
      57_590,
      5,
      'Boa tarde, seu João. Na tela do lote, toque em Manejo em lote, escolha Sanitário e depois Vacina. Dá para marcar todos os animais de uma vez.',
    ],
    [57_560, 'in', 'Achei aqui, obrigado'],
    [
      57_555,
      'bot',
      'Seu atendimento foi encerrado. Se precisar de algo, é só mandar uma mensagem por aqui.',
    ],
  ],
  431
);

export const supportThreads: Record<number, ThreadHistory> = {
  501: {
    conversations: [
      { id: 431, status: 'resolved', created_at: ago(57_600), count: anteriorJoao.length },
    ],
    messages: anteriorJoao.map((m) => ({ ...m, thread_conversation_id: 431 })),
  },
};
